// Sprint-009 B4 — Reports copy + shaping helpers (design-phase-b.md §5).
// Every string here is checked against `design-spec-today-card.md` §4's
// banned list — tab labels are short plain nouns (never a raw endpoint
// name), the date-range guard's copy never surfaces "422"/"400 days"/
// "span exceeded" (§8's table).

import type { OverdueAgingItemOut, OverdueAgingRowOut } from "../types";

export type ReportTab = "completions" | "overdue" | "health-log" | "workload";

export const REPORT_TABS: Array<{ key: ReportTab; label: string }> = [
  { key: "completions", label: "Completion rate" },
  { key: "overdue", label: "Overdue" },
  { key: "health-log", label: "Health log" },
  { key: "workload", label: "Workload" },
];

export const REPORTS_PAGE_SUBHEADING = "How the farm's been doing.";

export const REPORT_PANEL_SUBHEADINGS: Record<ReportTab, string> = {
  completions: "Who's getting chores done, by week.",
  // R-B0b-2 (UX-required) — this present-tense subheading IS the
  // explanation for why the shared date-range toolbar doesn't render on
  // this one tab (§5.1/§5.2) — no separate banner needed.
  overdue: "What's overdue right now.",
  "health-log": "Care and notes, across every animal.",
  workload: "What's scheduled, and what's getting done, by area.",
};

export const REPORT_EMPTY_MESSAGES: Record<ReportTab, string> = {
  completions: "Nothing logged for this range yet.",
  overdue: "Nothing overdue — nice work.",
  "health-log": "No notes logged for this range.",
  workload: "Nothing scheduled for this range yet.",
};

/** Sprint-011 A9 (design-gate usability review secondary finding UX-08/
 * UI-08/CD-09) — a friendlier empty state for the one case that reads
 * "broken report" rather than "nothing happened yet": a first-open panel,
 * still on its untouched default 30-day range, that comes back with zero
 * rows. Content/copy only — the 30-day default itself is unchanged
 * (pending the sprint-011 review confirmation). Never shown once the admin
 * has touched the date pickers (that's a genuinely-filtered, deliberate
 * empty range, not a first-run one) — the plain `REPORT_EMPTY_MESSAGES`
 * copy still covers that case. */
export const REPORT_FIRST_RUN_EMPTY_HINT =
  "Nothing in the last 30 days yet — try widening the date range above.";

/** §5.2 — client-side mirror of the server's own span guard (ADR-002 A5
 * decision 1.5, reused). Never surfaces "422"/"400 days" — this exact
 * sentence, governed, is the only copy shown for either a `from > to` or
 * an over-span violation. */
export const REPORT_DATE_RANGE_ERROR = "Pick a smaller date range — up to about a year at a time.";

const MAX_REPORT_SPAN_DAYS = 400;

export function isValidReportRange(fromIso: string, toIso: string): boolean {
  const from = new Date(`${fromIso}T00:00:00`);
  const to = new Date(`${toIso}T00:00:00`);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return false;
  if (from > to) return false;
  const spanDays = (to.getTime() - from.getTime()) / (24 * 60 * 60 * 1000);
  return spanDays <= MAX_REPORT_SPAN_DAYS;
}

/** Default range on first load — "last 30 days" (§5.2). */
export function defaultReportRange(today: Date = new Date()): { from: string; to: string } {
  const to = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const from = new Date(to);
  from.setDate(from.getDate() - 30);
  const toIso = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  return { from: toIso(from), to: toIso(to) };
}

/** Sprint-011 A9 — `true` when `range` is exactly `defaultReportRange()`
 * (the admin hasn't touched the date pickers yet), the precondition for
 * `reportEmptyMessage()`'s friendlier first-run hint below. */
export function isDefaultReportRange(
  range: { from: string; to: string },
  today: Date = new Date(),
): boolean {
  const def = defaultReportRange(today);
  return range.from === def.from && range.to === def.to;
}

/** Sprint-011 A9 — picks `REPORT_FIRST_RUN_EMPTY_HINT` only for the
 * genuinely-first-run case (zero rows AND still on the untouched default
 * range); every other empty case (a deliberately narrowed/shifted range
 * that also comes back empty) keeps the plain `REPORT_EMPTY_MESSAGES`
 * copy. Callers pass their own current row count and range — this stays
 * a pure function, no report-fetching state of its own. */
export function reportEmptyMessage(
  tab: ReportTab,
  rowCount: number,
  range: { from: string; to: string },
  today: Date = new Date(),
): string {
  if (rowCount === 0 && isDefaultReportRange(range, today)) return REPORT_FIRST_RUN_EMPTY_HINT;
  return REPORT_EMPTY_MESSAGES[tab];
}

/** One flat row per overdue item, §5.3's "Chore · Area · Overdue since ·
 * Age bucket" table — `OverdueAgingRowOut` groups by `(area, bucket)` with
 * an `items[]` array server-side (ADR-002 A5-R6); this flattens that
 * grouping back into one row per addressable item, carrying the parent
 * row's `area`/`bucket` down onto each. */
export interface OverdueFlatRow {
  key: string;
  kind: OverdueAgingItemOut["kind"];
  id: string;
  title: string;
  area: string | null;
  bucket: string;
  overdueSince: string;
}

export function flattenOverdueRows(rows: OverdueAgingRowOut[]): OverdueFlatRow[] {
  const flat: OverdueFlatRow[] = [];
  for (const row of rows) {
    for (const item of row.items) {
      flat.push({
        key: `${item.kind}:${item.id}`,
        kind: item.kind,
        id: item.id,
        title: item.title,
        area: row.area,
        bucket: row.bucket,
        overdueSince: item.overdue_since,
      });
    }
  }
  return flat;
}

/** §5.3's age-bucket filter chips, one per bucket present. Order matches
 * "oldest first" (the report's own default sort) so the chip row reads in
 * the same order as the table when no explicit sort is active. */
export const OVERDUE_AGE_BUCKET_ORDER = [">30d", "8-30d", "4-7d", "1-3d"];

export function overdueBucketSortIndex(bucket: string): number {
  const idx = OVERDUE_AGE_BUCKET_ORDER.indexOf(bucket);
  return idx === -1 ? OVERDUE_AGE_BUCKET_ORDER.length : idx;
}

export const HEALTH_LOG_ANIMAL_FILTER_LABEL = "Animal";
export const HEALTH_LOG_ALL_ANIMALS_OPTION = "All animals";
